/**
 * Helper centralizado para gerar Signed URLs do Supabase Storage.
 *
 * Regras:
 *  - NUNCA persistir o retorno no banco (URLs expiram).
 *  - Logar erros (não falhar em silêncio).
 *  - Retornar `null` quando não conseguir resolver — o componente decide o fallback visual.
 *
 * getSignedImageUrls usa a Edge Function `get-signed-urls` (service role) para
 * bypassar RLS no storage — necessário porque createSignedUrls via cliente anon
 * é bloqueado pela policy fotos_obras_select_company em alguns contextos.
 */
import { supabase } from '@/lib/supabase'

/** Default: 1h. Para preview de imagens em listas, suficiente e barato. */
export const DEFAULT_TTL = 3600

const EDGE_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`

/**
 * Signed URL para um único arquivo. Usa client direto (suficiente para
 * casos pontuais como auto-retry de imagem expirada).
 */
export async function getSignedImageUrl(
  bucket: string,
  path: string,
  expiresIn: number = DEFAULT_TTL,
): Promise<string | null> {
  if (!path) return null
  try {
    const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, expiresIn)
    if (error || !data?.signedUrl) {
      console.warn('[storage-urls] createSignedUrl falhou', { bucket, path, error: error?.message })
      return null
    }
    return data.signedUrl
  } catch (err) {
    console.error('[storage-urls] createSignedUrl exception', { bucket, path, err })
    return null
  }
}

/**
 * Extract the relative storage path from a full Supabase storage URL.
 * Returns the path portion after the bucket name, stripping duplicated
 * bucket-name prefixes (e.g. /public/bucket/bucket/file → "file").
 * Returns the original string unchanged if it is already a relative path.
 */
function normalizeStoragePath(pathOrUrl: string): string {
  if (!pathOrUrl.startsWith('http')) return pathOrUrl
  try {
    const urlObj = new URL(pathOrUrl)
    const m = urlObj.pathname.match(
      /\/storage\/v1\/object\/(?:public\/|sign\/)?([^/]+)\/(.+)/
    )
    if (!m) return pathOrUrl
    const bucket = m[1]
    let path = decodeURIComponent(m[2]).replace(/\?.*$/, '')
    if (path.startsWith(bucket + '/')) path = path.slice(bucket.length + 1)
    return path
  } catch {
    return pathOrUrl
  }
}

/**
 * Versão em lote via Edge Function `get-signed-urls` (service role key).
 * Retorna um Record<originalPath, signedUrl|null>.
 * Normaliza automaticamente paths que sejam URLs completas (registros antigos).
 */
export async function getSignedImageUrls(
  bucket: string,
  paths: string[],
  expiresIn: number = DEFAULT_TTL,
): Promise<Record<string, string | null>> {
  const cleanPaths = paths.filter(Boolean)
  if (!cleanPaths.length) return {}

  // Normalize: strip full URLs down to relative paths, keep mapping original→normalized
  const originalToNormalized: Record<string, string> = {}
  const normalizedToOriginal: Record<string, string> = {}
  for (const p of cleanPaths) {
    const normalized = normalizeStoragePath(p)
    originalToNormalized[p] = normalized
    normalizedToOriginal[normalized] = p
  }
  const normalizedPaths = [...new Set(Object.values(originalToNormalized))]

  const map: Record<string, string | null> = {}

  try {
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrls(normalizedPaths, expiresIn)

    if (error) {
      console.warn('[storage-urls] createSignedUrls erro', { bucket, error: error.message })
    }

    for (const item of data ?? []) {
      const normalizedPath = (item as any).path as string
      const signedUrl = (item as any).signedUrl as string | null
      const originalKey = normalizedToOriginal[normalizedPath] ?? normalizedPath
      map[originalKey] = signedUrl ?? null
    }
  } catch (err) {
    console.error('[storage-urls] getSignedImageUrls exception', { bucket, err })
  }

  // Garantir entrada para todo path solicitado (usando chave original)
  for (const p of cleanPaths) {
    if (!(p in map)) map[p] = null
  }

  return map
}
